import React, { useEffect, useState } from 'react';
import { Select, Tag } from 'antd';
import { formService } from '@iso/services';

const { Option } = Select;

// Forms authored for exactly this job. They're floated to the top of the list —
// any form can still be used as an assessment, but this is the one the taxonomy
// already has a name for.
const ASSESSMENT_PURPOSE = 'training_competency_assessment';

/**
 * Picker for the assessment form attached to a training asset or session.
 *
 * Deliberately a thin wrapper over Select rather than a modal: attaching an
 * assessment is a one-line decision inside a bigger form, not a workflow of its
 * own. Clearing it is meaningful — on an asset it means "no assessment", on a
 * session it means "inherit the asset's default" — so allowClear is always on
 * and the caller supplies the placeholder that says which.
 */
const AssessmentFormSelect = ({ propertyId, value, onChange, placeholder, disabled }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        // Passing no page/is_pagination is what the endpoint treats as "give me
        // every form" — a property has tens of forms, not thousands, and a
        // paged picker would hide the one the trainer wants behind a scroll
        // they can't see.
        const res = await formService.getList(propertyId);
        if (cancelled) return;
        const list = (res && res.forms) || [];
        // Only schema forms can be graded or rendered by the assessment page;
        // a PDF upload has nothing to answer.
        const usable = list.filter((f) => f.schema_json && Array.isArray(f.schema_json.steps) && f.schema_json.steps.length);
        usable.sort((a, b) => {
          const aIsAssessment = a.purpose === ASSESSMENT_PURPOSE ? 0 : 1;
          const bIsAssessment = b.purpose === ASSESSMENT_PURPOSE ? 0 : 1;
          if (aIsAssessment !== bIsAssessment) return aIsAssessment - bIsAssessment;
          return (a.name || '').localeCompare(b.name || '');
        });
        setForms(usable);
      } catch (e) {
        if (!cancelled) setForms([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [propertyId]);

  return (
    <Select
      allowClear
      showSearch
      optionFilterProp="children"
      loading={loading}
      disabled={disabled}
      placeholder={placeholder || 'No assessment'}
      value={value === null ? undefined : value}
      onChange={(v) => onChange(v === undefined ? null : v)}
    >
      {forms.map((f) => (
        <Option key={f.id} value={f.id}>
          {f.name}
          {f.purpose === ASSESSMENT_PURPOSE && (
            <Tag color="green" style={{ marginLeft: 8 }}>Assessment</Tag>
          )}
        </Option>
      ))}
    </Select>
  );
};

export default AssessmentFormSelect;
